import React, { useEffect, useRef, useState } from "react";
import { useLive } from "@/contexts/LiveDataContext";
import { liveTally, livePartyTally, liveTotals } from "@/lib/live";
import { party, subParty } from "@/lib/parties";

type ViewMode = "coalition" | "party";

/**
 * Election-night leader bar — Kerala-style horizontal bar chart
 * with Coalition / Party toggle.
 */
export default function LiveLeaderBar() {
  const LIVE = useLive();
  const coalitionTally = liveTally(LIVE);
  const partyTallyData = livePartyTally(LIVE);
  const totals = liveTotals(LIVE);
  const leader = coalitionTally[0];
  const majorityNeeded = LIVE.majorityNeeded;
  const twoThirdsMajority = Math.ceil((LIVE.totalSeats * 2) / 3);
  const leaderReachedMajority = leader && leader.total >= majorityNeeded;

  const [viewMode, setViewMode] = useState<ViewMode>("coalition");
  const tally = viewMode === "coalition" ? coalitionTally : partyTallyData;

  // Track previous totals per party to detect changes
  const prevTotals = useRef<Record<string, number>>({});
  const [animatingParties, setAnimatingParties] = useState<Set<string>>(new Set());

  useEffect(() => {
    const changed = new Set<string>();
    for (const t of tally) {
      const prev = prevTotals.current[t.party] ?? 0;
      if (t.total !== prev && prev !== 0) {
        changed.add(t.party);
      }
      prevTotals.current[t.party] = t.total;
    }
    if (changed.size > 0) {
      setAnimatingParties(changed);
      const timer = setTimeout(() => setAnimatingParties(new Set()), 600);
      return () => clearTimeout(timer);
    }
  }, [tally]);

  const maxSeats = LIVE.totalSeats;

  /** Get color and label for a tally row depending on view mode */
  function getRowMeta(code: string) {
    if (viewMode === "coalition") {
      const p = party(code);
      return { color: p.color, label: p.name };
    } else {
      // Party view — use subParty for component parties
      const sp = subParty(code);
      return { color: sp.color, label: sp.name };
    }
  }

  return (
    <div className="bg-card">
      {/* Header: LIVE badge + stats + leader headline */}
      <div className="px-4 py-2">
        {/* Row 1: LIVE badge + stat chips + last updated */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 bg-primary px-2 py-1 text-[11px] font-extrabold uppercase tracking-wider text-primary-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              Live
            </span>
            <span className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-[11px]">
              <span className="tnum font-bold text-foreground">{totals.counted}</span>
              <span className="text-muted-foreground">/ {LIVE.totalSeats} counted</span>
            </span>

          </div>
          <div className="hidden shrink-0 text-right text-[11px] text-muted-foreground sm:block">
            Last updated
            <div className="font-bold text-foreground">{LIVE.lastUpdated}</div>
          </div>
        </div>
        {/* Row 2: Leader headline */}
        <div className="mt-1.5 font-serif-ed text-base font-black leading-tight sm:text-xl text-center">
          {leader ? (
            <>
              <span style={{ color: party(leader.party).color }}>
                {party(leader.party).full}
              </span>{" "}
              {leaderReachedMajority ? "wins majority with" : "leads with"} {leader.total}{" "}
              {leader.total === 1 ? "seat" : "seats"}
            </>
          ) : (
            "Awaiting first results"
          )}
        </div>
      </div>

      {/* Coalition / Party toggle + chart */}
      <div className="px-4 py-3">
        {/* Toggle pills */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex rounded-full border border-neutral-200 overflow-hidden">
            <button
              onClick={() => setViewMode("coalition")}
              className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                viewMode === "coalition"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              Coalition
            </button>
            <button
              onClick={() => setViewMode("party")}
              className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                viewMode === "party"
                  ? "bg-primary text-primary-foreground"
                  : "bg-white text-neutral-600 hover:bg-neutral-50"
              }`}
            >
              Party
            </button>
          </div>
          <span className="text-[11px] font-bold text-foreground uppercase tracking-wide">
            Results<br />
            <span className="text-sm tnum">{totals.counted}/{maxSeats}</span>
          </span>
        </div>

        {/* Header row */}
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">
            Total: {maxSeats}
          </span>
        </div>

        {/* Bar chart rows — uses a two-column grid so labels share exact bar-track width */}
        <div className="relative">
          {/* Each bar row: [label col] [bar track] [count col] */}
          <div className="grid gap-x-2 gap-y-2 sm:gap-x-3" style={{ gridTemplateColumns: '60px 1fr 32px' }}>
            {tally.map((t) => {
              const barWidth = (t.total / maxSeats) * 100;
              const isAnimating = animatingParties.has(t.party);
              const { color, label } = getRowMeta(t.party);
              return (
                <React.Fragment key={t.party}>
                  {/* Party/coalition label */}
                  <div className="flex items-center">
                    <span
                      className="text-[12px] sm:text-[13px] font-extrabold leading-tight"
                      style={{ color }}
                    >
                      {label}
                    </span>
                  </div>
                  {/* Bar track */}
                  <div className="relative h-[18px] sm:h-5">
                    {/* Background track */}
                    <div className="absolute inset-0 rounded-sm bg-neutral-100 border border-neutral-200" />
                    {/* Filled bar */}
                    {t.total > 0 && (
                      <div
                        className={`absolute inset-y-0 left-0 rounded-sm overflow-hidden transition-all duration-500 ease-out ${isAnimating ? "animate-bar-pulse" : ""}`}
                        style={{ width: `${barWidth}%` }}
                      >
                        {/* Won portion (solid) */}
                        {t.won > 0 && (
                          <div
                            className="absolute inset-y-0 left-0"
                            style={{
                              width: `${(t.won / t.total) * 100}%`,
                              background: color,
                            }}
                          />
                        )}
                        {/* Leading portion (hatched/striped) */}
                        {t.leading > 0 && (
                          <div
                            className="absolute inset-y-0"
                            style={{
                              left: `${(t.won / t.total) * 100}%`,
                              width: `${(t.leading / t.total) * 100}%`,
                              background: color,
                              opacity: 0.5,
                              backgroundImage:
                                "repeating-linear-gradient(45deg, rgba(255,255,255,.3) 0 3px, transparent 3px 6px)",
                            }}
                          />
                        )}
                      </div>
                    )}
                    {/* Simple majority dashed line */}
                    <div
                      className="absolute top-0 bottom-0 z-10"
                      style={{ left: `${(majorityNeeded / maxSeats) * 100}%` }}
                    >
                      <div className="w-px h-full border-l border-dashed border-neutral-500" />
                    </div>
                    {/* Two-thirds majority dashed line */}
                    <div
                      className="absolute top-0 bottom-0 z-10"
                      style={{ left: `${(twoThirdsMajority / maxSeats) * 100}%` }}
                    >
                      <div className="w-px h-full border-l border-dashed border-neutral-400" />
                    </div>
                  </div>
                  {/* Seat count */}
                  <div className="flex items-center justify-end">
                    <span
                      className={`text-[13px] sm:text-sm font-extrabold tnum ${isAnimating ? "animate-count-bounce" : ""}`}
                      style={{ color }}
                    >
                      {t.total}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
            {/* Pending row (only in coalition view) */}
            {viewMode === "coalition" && totals.pending > 0 && (
              <React.Fragment>
                <div className="flex items-center">
                  <span className="text-[12px] sm:text-[13px] font-bold text-muted-foreground">
                    Pending
                  </span>
                </div>
                <div className="relative h-[18px] sm:h-5">
                  <div className="absolute inset-0 rounded-sm bg-neutral-100 border border-neutral-200" />
                  {/* Simple majority dashed line */}
                  <div
                    className="absolute top-0 bottom-0 z-10"
                    style={{ left: `${(majorityNeeded / maxSeats) * 100}%` }}
                  >
                    <div className="w-px h-full border-l border-dashed border-neutral-500" />
                  </div>
                  {/* Two-thirds majority dashed line */}
                  <div
                    className="absolute top-0 bottom-0 z-10"
                    style={{ left: `${(twoThirdsMajority / maxSeats) * 100}%` }}
                  >
                    <div className="w-px h-full border-l border-dashed border-neutral-400" />
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <span className="text-[13px] sm:text-sm font-bold tnum text-muted-foreground">
                    {totals.pending}
                  </span>
                </div>
              </React.Fragment>
            )}
            {/* Majority labels row — same grid column as bar tracks, guaranteed alignment */}
            <div /> {/* empty label column */}
            <div className="relative" style={{ minHeight: '34px' }}>
              {/* Simple majority label */}
              {/* Desktop: centered on line. Mobile: right edge anchored to line (grows left) */}
              <div
                className="absolute leading-none text-center -translate-x-full sm:-translate-x-1/2"
                style={{ left: `${(majorityNeeded / maxSeats) * 100}%` }}
              >
                <span className="hidden sm:block text-[8px] font-bold text-muted-foreground uppercase">Simple</span>
                <span className="hidden sm:block text-[8px] font-bold text-muted-foreground uppercase">Majority</span>
                <span className="sm:hidden block text-[7px] font-bold text-muted-foreground uppercase leading-tight whitespace-nowrap">Simple</span>
                <span className="block text-[10px] sm:text-[11px] font-extrabold text-foreground tnum sm:mt-0.5">{majorityNeeded}</span>
              </div>
              {/* Two-thirds majority label */}
              {/* Desktop: centered on line. Mobile: left edge anchored to line (grows right) */}
              <div
                className="absolute leading-none text-center translate-x-0 sm:-translate-x-1/2"
                style={{ left: `${(twoThirdsMajority / maxSeats) * 100}%` }}
              >
                <span className="hidden sm:block text-[8px] font-bold text-muted-foreground uppercase">Two-thirds</span>
                <span className="hidden sm:block text-[8px] font-bold text-muted-foreground uppercase">Majority</span>
                <span className="sm:hidden block text-[7px] font-bold text-muted-foreground uppercase leading-tight whitespace-nowrap">⅔ Maj.</span>
                <span className="block text-[10px] sm:text-[11px] font-extrabold text-foreground tnum sm:mt-0.5">{twoThirdsMajority}</span>
              </div>
            </div>
            <div /> {/* empty count column */}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-2.5 pt-2 border-t border-neutral-100">
            <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className="inline-block h-2.5 w-5 rounded-sm bg-neutral-700" /> Declared
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className="inline-block h-2.5 w-5 rounded-sm overflow-hidden" style={{ background: 'repeating-linear-gradient(45deg, rgba(100,100,100,0.3) 0px 3px, rgba(100,100,100,0.6) 3px 6px)', backgroundColor: 'rgba(100,100,100,0.15)' }} /> Leading
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
